// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Link } from '@/components/link';
import { OverflowTypography } from '@/components/overflow-typography';
import { TimelineAxis, TimelineAxisDensity } from '@/components/timeline-axis';
import { TimelineDateIndicator } from '@/components/timeline-date-indicator';
import { TimelineProvider } from '@/contexts/timeline-provider';
import { useTimelineContainer } from '@/hooks/use-timeline-container';
import { Project } from '@/project/api/types';
import { ProjectTimelineSegment } from '@/project/components/project-timeline-segment';
import { Box, Divider, Paper, Stack, alpha, useTheme } from '@mui/material';
import { PatternLines } from '@visx/pattern';
import { scaleTime } from '@visx/scale';
import { BarRounded } from '@visx/shape';
import { DateTime, Interval } from 'luxon';
import { useEffect, useId } from 'react';

const AXIS_HEIGHT = 40;
const UNIT_WIDTH = 50;
const BAR_HEIGHT = 20;
const BAR_MARGIN = 8;
const DENSITY: TimelineAxisDensity = 'month';

interface History {
  id: string;
  start: DateTime;
  end: DateTime | null;
}

interface Asset {
  id: string;
  key: string;
  serial: string;
  history: History[];
}

interface Props {
  project: Project;
  assets: Asset[];
  triggerRemeasure?: boolean;
}

export function ProjectAssignmentTimeline(props: Props) {
  const assetPatternId = useId();

  const theme = useTheme();

  const today = DateTime.now().startOf('day');

  const minimumDate = props.assets.reduce((dateTime, asset) => {
    return DateTime.min(dateTime, ...asset.history.map(({ start }) => start));
  }, props.project.startOn ?? today);

  const maximumDate = props.assets.reduce(
    (dateTime, asset) => {
      return DateTime.max(
        dateTime,
        ...asset.history.map(({ end }) => end ?? props.project.endOn ?? today)
      );
    },
    props.project.endOn ?? today.plus({ [DENSITY]: 1 })
  );

  const interval = Interval.fromDateTimes(minimumDate, maximumDate);

  const container = useTimelineContainer(interval, {
    density: DENSITY,
    unitWidth: UNIT_WIDTH,
  });

  // Trigger remeasurement when requested by parent
  useEffect(() => {
    if (props.triggerRemeasure) {
      container.triggerRemeasure();
    }
  }, [props.triggerRemeasure, container]);

  const height =
    AXIS_HEIGHT +
    BAR_MARGIN +
    BAR_HEIGHT * (props.assets.length + 1) +
    BAR_MARGIN * (props.assets.length + 1);

  const dateScale = scaleTime({
    range: [40, container.width - 40],
    domain: [minimumDate.toJSDate(), maximumDate.toJSDate()],
  });

  const marginBottom = `${BAR_MARGIN}px`;
  const spacing = `${BAR_MARGIN}px`;
  const labelHeight = BAR_HEIGHT;

  return (
    <Paper variant="outlined" sx={{ display: 'flex' }}>
      {props.assets.length > 0 && (
        <Stack
          useFlexGap
          justifyContent="end"
          paddingX={1}
          spacing={spacing}
          marginBottom={marginBottom}
        >
          {props.assets.map((asset) => (
            <Link
              key={asset.id}
              to="/assets/$assetKey"
              params={{ assetKey: asset.key }}
            >
              <OverflowTypography
                variant="body2"
                maxWidth="5rem"
                overflow="hidden"
                whiteSpace="nowrap"
                textOverflow="ellipsis"
                height={labelHeight}
              >
                {asset.serial}
              </OverflowTypography>
            </Link>
          ))}
        </Stack>
      )}
      {props.assets.length > 0 && <Divider flexItem orientation="vertical" />}
      <Box
        height={height}
        sx={{
          flex: 1,
          display: 'flex',
          overflowX: 'auto',
          overflowY: 'hidden',
        }}
      >
        <Box
          ref={container.ref}
          flexGrow={1}
          display="flex"
          alignItems="center"
          marginY={1}
        >
          <svg width={container.width} height={height}>
            <TimelineProvider>
              <TimelineAxis
                scale={dateScale}
                width={container.width}
                density={DENSITY}
                interval={interval}
                axisHeight={AXIS_HEIGHT}
                chartHeight={height}
              />
              <PatternLines
                id={assetPatternId}
                height={4}
                width={4}
                strokeWidth={1.5}
                stroke={alpha(theme.palette.primary.main, 0.5)}
                background={alpha(theme.palette.primary.light, 0.5)}
                orientation={['diagonal']}
              />
              <ProjectTimelineSegment
                y={AXIS_HEIGHT + BAR_MARGIN}
                height={BAR_HEIGHT}
                scale={dateScale}
                start={props.project.startOn ?? today}
                end={props.project.endOn ?? today}
                label={props.project.name}
              />
              <TimelineDateIndicator
                y={AXIS_HEIGHT}
                height={height}
                scale={dateScale}
                dateTime={today}
                color={theme.palette.primary.light}
              />
              {props.assets.map((asset, index) => {
                return (
                  <g key={asset.id}>
                    {asset.history.map(({ id, start, end }) => {
                      const isIndeterminate = end === null;
                      const endDateTime = end ?? today;
                      const y =
                        AXIS_HEIGHT +
                        BAR_MARGIN +
                        BAR_MARGIN * (index + 1) +
                        BAR_HEIGHT * (index + 1);

                      const startDate = start.startOf('day').toJSDate();
                      const x = Math.max(dateScale(startDate), BAR_MARGIN);
                      const width = dateScale(endDateTime.toJSDate()) - x;
                      const indeterminateWidth =
                        dateScale(maximumDate.toJSDate()) -
                        dateScale(today.toJSDate());

                      return (
                        <Link
                          to="/assets/$assetKey"
                          key={id}
                          params={{ assetKey: asset.key }}
                        >
                          {isIndeterminate && (
                            <BarRounded
                              right
                              radius={4}
                              x={dateScale(today.toJSDate())}
                              y={y}
                              width={indeterminateWidth}
                              height={BAR_HEIGHT}
                              fill={`url(#${assetPatternId})`}
                            />
                          )}
                          <BarRounded
                            left
                            right={!isIndeterminate}
                            radius={4}
                            x={x}
                            y={y}
                            width={width}
                            height={BAR_HEIGHT}
                            fill={theme.palette.primary.light}
                          />
                        </Link>
                      );
                    })}
                  </g>
                );
              })}
            </TimelineProvider>
          </svg>
        </Box>
      </Box>
    </Paper>
  );
}
